import { PropsWithChildren, useId, useMemo, useRef } from 'react';
import { LinearGradient } from '@visx/gradient';
import { Group } from '@visx/group';
import { BarStack, Line } from '@visx/shape';

import { XAxis, YAxis } from '@components/patterns/LineChart/Axes';
import {
  ANNOTATION_DELTA,
  ANNOTATION_WIDTH,
  PATH_STROKE_DELTA,
  PATH_STROKE_WIDTH,
} from '@components/patterns/LineChart/constants';
import {
  LineChartContext,
  LineChartContextValue,
} from '@components/patterns/LineChart/context';
import { HoverHighlight } from '@components/patterns/LineChart/HoverHighlight';
import { LineChartSkeleton } from '@components/patterns/LineChart/Skeleton';
import {
  AnnotationTooltip,
  DataTooltip,
  TooltipContext,
  TooltipData,
} from '@components/patterns/LineChart/Tooltip';
import { TooltipPortal } from '@components/patterns/LineChart/TooltipPortal';
import {
  LineChartAnnotation,
  LineChartData,
  LineChartSeries,
} from '@components/patterns/LineChart/types';
import { useColors } from '@components/patterns/LineChart/use-colors';
import { useData } from '@components/patterns/LineChart/use-data';
import { useInteractions } from '@components/patterns/LineChart/use-interactions';
import { useMeasure } from '@components/patterns/LineChart/use-measure';
import { useScales } from '@components/patterns/LineChart/use-scales';
import { useTooltip } from '@components/patterns/LineChart/use-tooltip';
import { cn } from '@components/primitives/utilities';

import { asChartSlot, ChartSlot } from './slots';

import css from './styles.module.css';

function getAnnotationX<Annotation extends LineChartAnnotation>(
  series: LineChartSeries<any, Annotation>[],
  search: TooltipData['annotation']
) {
  if (!search) return 0;
  const match = series.find((s) => s.key === search.seriesKey);
  const annotation = match?.annotations?.at(search.index);
  return annotation?.x ?? 0;
}

export type LineChartDataClickHandler<Data extends LineChartData> = (
  data: Data[],
  index: number
) => void;

export interface LineChartProps<
  Data extends LineChartData,
  Annotation extends LineChartAnnotation,
> extends PropsWithChildren {
  id?: string;
  className?: string;
  series: LineChartSeries<Data, Annotation>[];
  onDataClick?: LineChartDataClickHandler<Data>;
  variant?: 'stacked';
}

function Root<
  Data extends LineChartData,
  Annotation extends LineChartAnnotation = LineChartAnnotation,
>({
  className,
  series,
  children,
  id: propId,
  onDataClick,
}: LineChartProps<Data, Annotation>) {
  const autoId = useId();
  const id = propId ?? autoId;

  const ref = useRef<SVGSVGElement>(null);
  const rect = useMeasure(ref, { top: 12 });

  const getColors = useColors(series);
  const { data, keys, meta } = useData(series);
  const scales = useScales(meta, rect);

  const tooltip = useTooltip(series, scales);
  const interactions = useInteractions(series, scales, {
    onClick: onDataClick,
  });

  const context = useMemo<LineChartContextValue<Data, Annotation>>(
    () => ({ series, rect, scales }),
    [series, rect, scales]
  );

  return (
    <LineChartContext.Provider value={context}>
      <svg ref={ref} id={id} className={cn(css.svg, className)}>
        {/* Define gradients for each series */}
        <defs>
          {series.map((s) => {
            const colors = getColors(s.key);
            return (
              <LinearGradient
                key={colors.key}
                id={`${id}-gradient-${colors.key}`}
                from={colors.fill.from}
                to={colors.fill.to}
              />
            );
          })}
        </defs>

        {/* Hover highlight for an annotation */}
        {tooltip.isOpen && tooltip.data?.annotation && (
          <HoverHighlight
            type="annotation"
            x={
              scales.x(getAnnotationX(series, tooltip.data.annotation)) -
              ANNOTATION_DELTA
            }
            y={0}
            width={ANNOTATION_WIDTH}
            height={rect.marginTop + rect.innerHeight}
          />
        )}

        {/* Hover highlight for a data point */}
        {tooltip.isOpen && (
          <HoverHighlight
            type="data"
            x={scales.index(tooltip.data.index)}
            y={0}
            width={rect.innerWidth / (meta.maxIndex + 1)}
            height={rect.marginTop + rect.innerHeight}
          />
        )}

        {/* Render axes */}
        <ChartSlot name="axis">{children}</ChartSlot>

        {/* Render the stacked data */}
        <Group id={`${id}-data`}>
          <BarStack
            color={(key) => `url(#${id}-gradient-${key})`}
            data={data}
            keys={keys}
            x={(d) => d.x}
            value={(d, key) => d.values[key]}
            xScale={scales.x}
            yScale={scales.y}
          >
            {(stacks) =>
              stacks.map((stack) => {
                const colors = getColors(stack.key);
                return (
                  <g key={stack.key} id={`${id}-data-${stack.key}`}>
                    {stack.bars.map((bar) => {
                      // If the current column is empty, only render the last bar
                      const isEmpty = data[bar.index].y === 0;
                      if (isEmpty && stack.index < stacks.length - 1) {
                        return null;
                      }

                      // If the column is not empty but the current stack has no value, hide the bar
                      if (!isEmpty && bar.height === 0) {
                        return null;
                      }

                      // Calculate uniform bar width based on index spacing
                      const width = rect.innerWidth / (meta.maxIndex + 1);
                      // Position bar so its left edge aligns with the index position
                      // The bar's x value represents the center, so we need to adjust
                      const x = scales.index(bar.index);

                      // This ensures the top line is (at the very least) flush with the y-axis
                      const [maxY] = scales.y.range();
                      const lineY = Math.min(bar.y + PATH_STROKE_DELTA, maxY);

                      return (
                        <g key={bar.index}>
                          <line
                            x1={x}
                            x2={x + width}
                            y1={lineY}
                            y2={lineY}
                            stroke={colors.stroke}
                            strokeWidth={PATH_STROKE_WIDTH}
                          />
                          <rect
                            x={x}
                            y={bar.y}
                            width={width}
                            height={Math.max(0, bar.height)}
                            fill={bar.color}
                          />
                        </g>
                      );
                    })}
                  </g>
                );
              })
            }
          </BarStack>
        </Group>

        {/* Render annotations */}
        <Group id={`${id}-annotations`}>
          {series.map(({ key, annotations }) => {
            if (!annotations?.length) return null;
            const colors = getColors(key);
            return (
              <g key={key} id={`${id}-annotations-${key}`}>
                {annotations.map((annotation) => {
                  return (
                    <Line
                      key={annotation.x}
                      x1={scales.x(annotation.x)}
                      x2={scales.x(annotation.x)}
                      y1={PATH_STROKE_WIDTH}
                      y2={rect.marginTop + rect.innerHeight - PATH_STROKE_WIDTH}
                      stroke={colors.annotation}
                      strokeWidth={PATH_STROKE_WIDTH}
                      strokeDasharray="1.5,3"
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            );
          })}
        </Group>

        {/* Hover area for tooltip */}
        <rect
          x={0}
          y={0}
          width={rect.width}
          height={rect.height}
          fill="transparent"
          onPointerMove={tooltip.onPointerMove}
          onPointerLeave={tooltip.onPointerLeave}
          onClick={interactions.onClick}
        />

        {/* Tooltip content */}
        {tooltip.isOpen && (
          <TooltipPortal
            containerRef={ref}
            left={tooltip.left}
            top={tooltip.top}
          >
            <TooltipContext.Provider value={tooltip.data}>
              <ChartSlot name="tooltip">{children}</ChartSlot>
            </TooltipContext.Provider>
          </TooltipPortal>
        )}
      </svg>
    </LineChartContext.Provider>
  );
}

export const LineChart = Object.assign(Root, {
  Skeleton: LineChartSkeleton,

  YAxis: asChartSlot('axis', YAxis),
  XAxis: asChartSlot('axis', XAxis),

  // We need to force-type these to keep the generics of the original components
  DataTooltip: asChartSlot('tooltip', DataTooltip) as typeof DataTooltip,
  AnnotationTooltip: asChartSlot(
    'tooltip',
    AnnotationTooltip
  ) as typeof AnnotationTooltip,
});

export type {
  XAxisProps as LineChartXAxisProps,
  YAxisProps as LineChartYAxisProps,
} from './Axes';
export type { LineChartSkeletonProps } from './Skeleton';
export type {
  AnnotationTooltipProps as LineChartAnnotationTooltipProps,
  LineChartAnnotationTooltipRenderProps,
  DataTooltipProps as LineChartDataTooltipProps,
  LineChartDataTooltipRenderProps,
} from './Tooltip';
export type {
  LineChartAnnotation,
  LineChartData,
  LineChartSeries,
} from './types';
